import { z } from 'zod';
import { DEAL_MATCH_STRATEGIES } from './deal-match-strategy.js';
import { SortDirection, SortDirectionSchema } from './pagination.js';
/**
 * Email Synchronization Types
 *
 * TypeScript interfaces and Zod schemas for the email sync system.
 * Email body content is stored in S3, never in the database.
 *
 * Privacy Design:
 *   - userId is CRITICAL for privacy enforcement
 *   - All email queries must include userId filter
 *   - Admins cannot access email content, only aggregate metrics
 *
 * Related Stories: 3.2
 */
// =============================================================================
// Email Provider
// =============================================================================
export const EmailProvider = {
    GOOGLE: 'google',
    MICROSOFT: 'microsoft',
};
export const EmailProviderSchema = z.enum(Object.values(EmailProvider));
// =============================================================================
// Email Match Strategy
// =============================================================================
export const EmailMatchStrategy = {
    DOMAIN: 'domain',
    CONTACT: 'contact',
    MEETING: 'meeting',
    MANUAL: 'manual',
    THREAD: 'thread',
    TITLE_KEYWORD: 'title_keyword',
};
export const EmailMatchStrategySchema = z.enum(Object.values(EmailMatchStrategy));
// =============================================================================
// Sync Status
// =============================================================================
export const SyncStatus = {
    PENDING: 'pending',
    SYNCING: 'syncing',
    SUCCESS: 'success',
    ERROR: 'error',
};
export const SyncStatusSchema = z.enum(Object.values(SyncStatus));
export const EmailSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    userId: z.string().uuid(),
    provider: EmailProviderSchema,
    messageId: z.string().min(1),
    threadId: z.string().nullable().optional(),
    rfc822MessageId: z.string().nullable().optional(),
    inReplyTo: z.string().nullable().optional(),
    references: z.string().nullable().optional(),
    spamReportedAt: z.string().datetime().nullable().optional(),
    reportedReason: z.string().nullable().optional(),
    subject: z.string().max(500).nullable().optional(),
    fromEmail: z.string().email().max(255),
    fromName: z.string().max(255).nullable().optional(),
    toEmails: z.array(z.string().email()),
    ccEmails: z.array(z.string().email()).nullable().optional(),
    sentAt: z.string().datetime(),
    isOutbound: z.boolean(),
    clientId: z.string().uuid().nullable().optional(),
    clientName: z.string().nullable().optional(),
    clientLifecycle: z.enum(['active', 'archived', 'deleted']).optional(),
    dealId: z.string().uuid().nullable().optional(),
    dealName: z.string().nullable().optional(),
    dealLifecycle: z.enum(['open', 'closed', 'archived', 'deleted']).optional(),
    dealMatchStrategy: z.enum(DEAL_MATCH_STRATEGIES).nullable().optional(),
    dealConfidenceScore: z.number().min(0).max(100).nullable().optional(),
    dealLinkReason: z.string().nullable().optional(),
    meetingId: z.string().uuid().nullable().optional(),
    meetingName: z.string().nullable().optional(),
    actionItemId: z.string().uuid().nullable().optional(),
    matchStrategy: EmailMatchStrategySchema.nullable().optional(),
    ownerName: z.string().optional(),
    redacted: z.boolean().optional(),
    isShared: z.boolean().optional(),
    breakGlassActive: z.boolean().optional(),
    breakGlassExpiresAt: z.string().datetime().optional(),
    bodyHtml: z.string().nullable().optional(),
    bodyS3Key: z.string().min(1).optional(),
    snippet: z.string().max(500).nullable().optional(),
    isRead: z.boolean().optional(),
    isArchived: z.boolean().optional(),
    hasInboundResponse: z.boolean().optional(),
    threadMessageCount: z.number().int().nonnegative().optional(),
    isDeleted: z.boolean().optional(),
    syncedAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    attachments: z.array(z.lazy(() => EmailAttachmentSchema)).optional(),
});
export const EmailAttachmentSchema = z.object({
    id: z.string().uuid(),
    emailId: z.string().uuid(),
    filename: z.string().min(1).max(255),
    fileType: z.string().max(100).nullable().optional(),
    sizeBytes: z.number().int().nullable().optional(),
    providerAttachmentId: z.string().min(1).optional(),
    fileId: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/i).optional(),
    deliveryLinks: z.array(z.lazy(() => z.object({
        id: z.string().min(1),
        fileId: z.string().min(1),
        emailId: z.string().min(1).nullable().optional(),
        draftId: z.string().min(1).nullable().optional(),
        token: z.string().min(1),
        expiresAt: z.string().datetime(),
        revokedAt: z.string().datetime().nullable().optional(),
        createdAt: z.string().datetime(),
    }))).optional(),
    security: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.string().datetime(),
});
export const EmailThreadSchema = z.object({
    threadId: z.string().min(1),
    subject: z.string().nullable().optional(),
    emails: z.array(EmailSchema),
    participantEmails: z.array(z.string().email()),
    latestSentAt: z.string().datetime(),
    messageCount: z.number().int().positive(),
});
export const EmailSyncStatusSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    userId: z.string().uuid(),
    provider: EmailProviderSchema,
    lastSyncAt: z.string().datetime().nullable().optional(),
    syncStatus: SyncStatusSchema.default('pending'),
    lastHistoryId: z.string().nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const EmailMetricsDailySchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    userId: z.string().uuid(),
    clientId: z.string().uuid().nullable().optional(),
    metricDate: z.string().date(),
    emailsSent: z.number().int().min(0).default(0),
    emailsReceived: z.number().int().min(0).default(0),
    avgResponseMs: z.number().int().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateEmailRequestSchema = z.object({
    provider: EmailProviderSchema,
    mailboxIdentity: z.string().min(1).nullable().optional(),
    messageId: z.string().min(1),
    threadId: z.string().nullable().optional(),
    rfc822MessageId: z.string().nullable().optional(),
    inReplyTo: z.string().nullable().optional(),
    references: z.string().nullable().optional(),
    subject: z.string().max(500).nullable().optional(),
    fromEmail: z.string().email().max(255),
    fromName: z.string().max(255).nullable().optional(),
    toEmails: z.array(z.string().email()),
    ccEmails: z.array(z.string().email()).nullable().optional(),
    sentAt: z.string().datetime(),
    isOutbound: z.boolean(),
    bodyHtml: z.string().nullable().optional(),
    bodyS3Key: z.string().min(1).optional(),
    snippet: z.string().max(500).nullable().optional(),
    meetingId: z.string().uuid().nullable().optional(),
    calendarEventUid: z.string().min(1).nullable().optional(),
    clientId: z.string().uuid().nullable().optional(),
    matchStrategy: EmailMatchStrategySchema.nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
    actionItemId: z.string().uuid().nullable().optional(),
    source: z.enum(['sync', 'direct']).optional(),
});
export const LinkEmailRequestSchema = z.object({
    clientId: z.string().uuid(),
    dealId: z.string().uuid().nullable().optional(),
    matchStrategy: EmailMatchStrategySchema.optional(),
    replaceExisting: z.boolean().optional(),
});
// Trigger Sync
export const SyncScope = {
    SELF: 'self',
    ORG: 'org',
};
export const TriggerSyncRequestSchema = z
    .object({
    provider: EmailProviderSchema.optional(),
    force: z.boolean().default(false),
    mode: z.enum(['incremental', 'full']).optional(),
    scope: z.enum(Object.values(SyncScope)).optional().default('self'),
})
    .superRefine((value, ctx) => {
    if (value.scope !== 'org' && !value.provider) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['provider'],
            message: 'Provider is required for self sync',
        });
    }
});
export const EmailSearchQuerySchema = z.object({
    clientId: z.string().uuid().optional(),
    dealId: z.string().uuid().optional(),
    meetingId: z.string().uuid().optional(),
    contactEmail: z.string().email().optional(),
    actionItemId: z.string().uuid().optional(),
    threadId: z.string().optional(),
    search: z.string().optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    isLinked: z.boolean().optional(),
    isDeleted: z.boolean().optional(),
    isOutbound: z.boolean().optional(),
    isArchived: z.boolean().optional(),
    isRead: z.boolean().optional(),
    userIds: z.array(z.string().uuid()).optional(),
    sharedWithMe: z.boolean().optional(),
    shareDirection: z.enum(['by_me', 'with_me']).optional(),
    page: z.number().int().positive().default(1),
    pageSize: z.number().int().positive().max(100).default(50),
    cursor: z.string().optional(),
    sortBy: z.enum(['sentAt', 'subject']).default('sentAt'),
    sortDir: SortDirectionSchema.default(SortDirection.DESC),
});
// =============================================================================
// Communication Timeline (Unified meetings + emails)
// =============================================================================
export const CommunicationType = {
    MEETING: 'meeting',
    EMAIL: 'email',
    NOTE: 'note',
    ACTION_ITEM_CREATED: 'action_item_created',
    ACTION_ITEM_COMPLETED: 'action_item_completed',
    ACTION_ITEMS: 'action_items',
};
export const CommunicationTimelineQuerySchema = z.object({
    clientId: z.string().uuid(),
    dealId: z.string().uuid().optional(),
    type: z.enum(Object.values(CommunicationType)).optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    search: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.number().int().positive().max(100).default(50),
});
// =============================================================================
// Story 3.3: Email Composition Types
// =============================================================================
// =============================================================================
// Draft Status
// =============================================================================
export const DraftStatus = {
    DRAFT: 'draft',
    SCHEDULED: 'scheduled',
    SENDING: 'sending',
    SENT: 'sent',
    FAILED: 'failed',
};
export const DraftStatusSchema = z.enum(Object.values(DraftStatus));
export const EmailDraftSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    userId: z.string().uuid(),
    clientId: z.string().uuid().nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
    meetingId: z.string().uuid().nullable().optional(),
    actionItemId: z.string().uuid().nullable().optional(),
    toEmails: z.array(z.string().email()),
    ccEmails: z.array(z.string().email()).nullable().optional(),
    bccEmails: z.array(z.string().email()).nullable().optional(),
    subject: z.string().max(500).default(''),
    bodyHtml: z.string().default(''),
    bodyPlain: z.string().default(''),
    linkSectionHtml: z.string().nullable().optional(),
    linkExpiresAt: z.string().datetime().nullable().optional(),
    deliveryLinks: z.array(z.lazy(() => z.object({
        id: z.string().min(1),
        fileId: z.string().min(1),
        emailId: z.string().min(1).nullable().optional(),
        draftId: z.string().min(1).nullable().optional(),
        token: z.string().min(1),
        expiresAt: z.string().datetime(),
        revokedAt: z.string().datetime().nullable().optional(),
        createdAt: z.string().datetime(),
    }))).optional(),
    replyToEmailId: z.string().uuid().nullable().optional(),
    replyToThreadId: z.string().nullable().optional(),
    attachments: z.array(z.lazy(() => DraftAttachmentSchema)).default([]),
    status: DraftStatusSchema.default('draft'),
    scheduledAt: z.string().datetime().nullable().optional(),
    memoryRevision: z.string().nullable().optional(),
    memoryFactIds: z.array(z.string().uuid()).optional(),
    lastSendAttempt: z.object({
        status: z.enum(['provider_rejected', 'provider_accepted', 'delivery_unknown', 'failed']),
        provider: z.enum(Object.values(EmailProvider)).optional(),
        reason: z.string().optional(),
        attemptedAt: z.string().datetime(),
    }).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const DraftAttachmentSchema = z.object({
    id: z.string().uuid(),
    draftId: z.string().uuid(),
    filename: z.string().min(1).max(255),
    fileType: z.string().max(100).nullable().optional(),
    sizeBytes: z.number().int().nullable().optional(),
    fileId: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/i).optional(),
    quarantineUploadId: z.string().uuid().optional(),
    downloadUrl: z.string().url().optional(),
    createdAt: z.string().datetime(),
    security: z.record(z.string(), z.unknown()).optional(),
});
// =============================================================================
// Provider-Aware Attachment Limits
// =============================================================================
export const EMAIL_ATTACHMENT_LIMITS = {
    google: {
        maxPerFile: 25 * 1024 * 1024, // 25MB
        maxTotal: 25 * 1024 * 1024, // 25MB total (Gmail API is 35MB encoded, ~25MB actual)
        label: 'Gmail',
        errorMessage: 'Gmail has a 25MB attachment limit',
    },
    microsoft: {
        maxPerFile: 150 * 1024 * 1024, // 150MB
        maxTotal: 150 * 1024 * 1024, // 150MB total (Exchange Online, admin-configurable)
        chunkThreshold: 3 * 1024 * 1024, // Use chunked upload above 3MB
        chunkSize: 4 * 1024 * 1024, // 4MB per chunk
        label: 'Outlook',
        errorMessage: 'Outlook has a 150MB attachment limit',
    },
};
export const EmailSignatureSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    userId: z.string().uuid(),
    provider: EmailProviderSchema,
    signatureHtml: z.string().default(''),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const SendEmailRequestSchema = z.object({
    toEmails: z.array(z.string().email()).min(1, 'At least one recipient required'),
    ccEmails: z.array(z.string().email()).nullable().optional(),
    bccEmails: z.array(z.string().email()).nullable().optional(),
    subject: z.string().min(1, 'Subject is required').max(500),
    bodyHtml: z.string().min(1, 'Email body is required'),
    bodyPlain: z.string().default(''),
    replyToEmailId: z.string().uuid().nullable().optional(),
    clientId: z.string().uuid().nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
    meetingId: z.string().uuid().nullable().optional(),
    actionItemId: z.string().uuid().nullable().optional(),
    scheduledAt: z.string().datetime().nullable().optional(),
    attachmentIds: z.array(z.string().uuid()).optional(),
    linkExpiresAt: z.string().datetime().nullable().optional(),
    includeSignature: z.boolean().default(true),
    linkSectionHtml: z.string().nullable().optional(),
});
export const CreateDraftRequestSchema = z.object({
    clientId: z.string().uuid().nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
    meetingId: z.string().uuid().nullable().optional(),
    actionItemId: z.string().uuid().nullable().optional(),
    toEmails: z.array(z.string().email()).optional().default([]),
    ccEmails: z.array(z.string().email()).nullable().optional(),
    bccEmails: z.array(z.string().email()).nullable().optional(),
    subject: z.string().max(500).optional().default(''),
    bodyHtml: z.string().optional().default(''),
    bodyPlain: z.string().optional().default(''),
    replyToEmailId: z.string().uuid().nullable().optional(),
    replyToThreadId: z.string().nullable().optional(),
    memoryRevision: z.string().nullable().optional(),
    memoryFactIds: z.array(z.string().uuid()).optional(),
    linkSectionHtml: z.string().nullable().optional(),
    linkExpiresAt: z.string().datetime().nullable().optional(),
});
export const UpdateDraftRequestSchema = z.object({
    toEmails: z.array(z.string().email()).optional(),
    ccEmails: z.array(z.string().email()).nullable().optional(),
    bccEmails: z.array(z.string().email()).nullable().optional(),
    subject: z.string().max(500).optional(),
    bodyHtml: z.string().optional(),
    bodyPlain: z.string().optional(),
    clientId: z.string().uuid().nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
    meetingId: z.string().uuid().nullable().optional(),
    linkSectionHtml: z.string().nullable().optional(),
    linkExpiresAt: z.string().datetime().nullable().optional(),
});
export const ScheduleDraftRequestSchema = z.object({
    scheduledAt: z.string().datetime(),
});
export const UpdateSignatureRequestSchema = z.object({
    signatureHtml: z.string(),
});
// =============================================================================
// Story 4.2: Email AI Types
// =============================================================================
/**
 * Email AI action types for text transformation
 */
export const EmailAIAction = {
    FORMAL: 'formal',
    TASK_FOCUSED: 'task_focused',
    SHORTEN: 'shorten',
    ELABORATE: 'elaborate',
};
export const EmailAIActionSchema = z.enum(Object.values(EmailAIAction));
/**
 * Email sentiment types for analysis
 */
export const EmailSentiment = {
    FRIENDLY: 'friendly',
    FORMAL: 'formal',
    URGENT: 'urgent',
    NEUTRAL: 'neutral',
};
export const EmailSentimentSchema = z.enum(Object.values(EmailSentiment));
export const TransformTextRequestSchema = z.object({
    text: z.string().min(1, 'Text is required'),
    action: EmailAIActionSchema,
});
export const TransformTextResponseSchema = z.object({
    transformedText: z.string(),
    action: EmailAIActionSchema,
});
export const AnalyzeEmailRequestSchema = z.object({
    subject: z.string().max(500),
    body: z.string().min(1, 'Email body is required'),
});
export const EmailAnalysisSchema = z.object({
    suggestions: z.array(z.string()),
    sentiment: EmailSentimentSchema,
    engagementScore: z.number().min(0).max(100),
});
export const AnalyzeEmailResponseSchema = z.object({
    analysis: EmailAnalysisSchema,
});
export const EmailTranslateRequestSchema = z.object({
    emailId: z.string().min(1, 'Email ID is required'),
    targetLanguage: z.string().min(2).optional(),
});
export const EmailTranslateResponseSchema = z.object({
    translatedText: z.string(),
    translatedHtml: z.string().optional(),
    detectedLanguage: z.string().optional(),
    targetLanguage: z.string().optional(),
});
// =============================================================================
// Email AI Generation (Story 4.2+)
// =============================================================================
export const GenerateEmailRequestSchema = z.object({
    context: z.string().min(1).max(2000),
    replyToEmailId: z.string().uuid().optional(),
    threadId: z.string().optional(),
    existingSubject: z.string().optional(),
    existingBody: z.string().optional(),
    clientId: z.string().uuid().optional(),
    dealId: z.string().uuid().optional(),
});
